import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getWorkflowStageTypes, createWorkflowStageType, deleteWorkflowStageType } from "../../../actions/approvalWorkflow";
import { getCompany } from "../../../actions/company";
import Modal from "../../common/Modal/Modal";
import { useSubmitGuard } from "../../common/Helpers";

export default function StageNamesList() {
    const dispatch = useDispatch();
    const { isSubmitting, guardedSubmit } = useSubmitGuard();

    const stageTypes = useSelector(state => state.approvalWorkflow.stageTypes);
    const company = useSelector(state => state.company.company);
    const isCompanyAdmin = useSelector(state => state.auth.user.croles && state.auth.user.croles["Company Admin"]);

    const [deletingStageType, setDeletingStageType] = useState(null);
    const [addingStageType, setAddingStageType] = useState(false);
    const [newStageName, setNewStageName] = useState("");

    useEffect(() => {
        dispatch(getCompany());
        dispatch(getWorkflowStageTypes());
    }, [dispatch]);

    const handleAdd = guardedSubmit((e) => {
        e.preventDefault();
        const trimmed = newStageName.trim();
        if (!trimmed) return undefined;
        return dispatch(createWorkflowStageType(trimmed)).then((saved) => {
            if (saved) {
                setAddingStageType(false);
                setNewStageName("");
            }
        });
    });

    const handleDelete = () => {
        dispatch(deleteWorkflowStageType(deletingStageType.uuid));
        setDeletingStageType(null);
    };

    //company tier isn't known yet -- render nothing rather than flashing the Enterprise-only management UI by default
    if (!company.uuid) {
        return <></>;
    }

    //Basic-tier companies only ever use the 2 global templates -- no stage-name management for them
    if (company.is_basic) {
        return (
            <div className="gap-below-s settings">
                <h4 className="content1 multiline light-black">Custom Stage Names are available on the Enterprise plan.</h4>
            </div>
        );
    }

    const globalStageTypes = stageTypes.filter(t => !t.fk_company);
    const companyStageTypes = stageTypes.filter(t => t.fk_company);

    return (
        <div className="gap-below-s settings">
            <div className="subhead-wrapper flex-row space-btwn">
                <h2 className="title2 heavy iblock gap-right-xs">Stage Names</h2>
                {isCompanyAdmin &&
                    <button type="button" className="btn pri iblock mini" onClick={() => setAddingStageType(true)}>New Stage Name</button>
                }
            </div>
            <div className="table-wrapper">
                <div className="grid">
                    <div className="grid-child" style={{ gridColumn: "span 4" }}>
                        <h3 className="content2 heavy-grey gap-below-xxs">Standard Stage Names</h3>
                        {globalStageTypes.map(t => (
                            <h4 key={t.uuid} className="content1 multiline light-black gap-below-xs">{t.name}</h4>
                        ))}

                        <h3 className="content2 heavy-grey gap-below-xxs gap-above-m">Your Stage Names</h3>
                        {companyStageTypes.length < 1 && <h4 className="content1 multiline light">No custom Stage Names yet.</h4>}
                        {companyStageTypes.map(t => (
                            <div key={t.uuid} className="flex-row space-btwn gap-below-xxs">
                                <h4 className="content1 multiline light-black">{t.name}</h4>
                                {isCompanyAdmin &&
                                    <button type="button" className="btn ter mini iblock" onClick={() => setDeletingStageType(t)}>Delete</button>
                                }
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {addingStageType &&
            <Modal blurBack={true} hasCloseButton={true} hideLogo handleClose={() => setAddingStageType(false)} >
                <div className="flex-column w100" >
                    <form onSubmit={handleAdd} className="form" >
                        <h3 className="title2 gap-below-m heavy t-center">New Stage Name</h3>
                        <div className="inputs-wrapper gap-below-m">
                            <div className="input-basic">
                                <input
                                    type="text"
                                    name="new_stage_name"
                                    id="new_stage_name"
                                    value={newStageName}
                                    onChange={(e) => setNewStageName(e.target.value)}
                                    autoFocus
                                    required
                                />
                                <label htmlFor="new_stage_name">Name (e.g. Manager Approval)<span> *</span></label>
                            </div>
                        </div>
                        <div className="flex-row">
                            <button type="submit" className="btn pri expand gap-right-xs" disabled={isSubmitting}>{isSubmitting ? "Please wait..." : "Add Stage Name"}</button>
                            <button type="button" className="btn sec expand" disabled={isSubmitting} onClick={() => setAddingStageType(false)}>Cancel</button>
                        </div>
                    </form>
                </div>
            </Modal>}

            {deletingStageType &&
            <Modal blurBack={true} hasCloseButton={true} handleClose={() => setDeletingStageType(null)} >
                <div className="flex-column space-btwn flex1 w100" >
                    {/* Modal renders its own slogan as a direct flex child of .modal (which centers it),
                        but wraps props.children in a plain, non-flex "w100" div -- so the .slogan class's
                        width:80% would just left-align here instead of centering. Overriding to 100% width
                        lets text-align:center do the centering the same way the real slogan gets it. */}
                    <p className="slogan g5-text gap-below-m" style={{ width: "100%" }}>
                        Delete &quot;{deletingStageType.name}&quot;? Templates already built with it are not affected.
                    </p>
                    <div className="flex-row ">
                        <button className="iblock btn expand pri gap-right-xs" onClick={handleDelete} >Delete</button>
                        <button className="iblock btn expand sec" onClick={() => setDeletingStageType(null)} >Cancel</button>
                    </div>
                </div>
            </Modal>}
        </div>
    );
}
